import { PIPELINE_DEMO_SCENE_NUMBER } from "../home-hero/pipeline-demo-shared";
import { DEMO_APPLIED_TAG_STRUCTURES } from "../home-hero/script-editor-demo/script-editor-demo.constants";

import type {
  RewindBeat,
  RewindBeatId,
  RewindCategoryId,
  RewindTagId,
} from "./home-rewind.types";

/** Beat ranges as a share of hero scroll. The timeline bar shows every beat but the reveal. */
export const REWIND_BEATS: RewindBeat[] = [
  { id: "screen", start: 0, end: 0.15 },
  { id: "set", start: 0.15, end: 0.28 },
  { id: "breakdown", start: 0.28, end: 0.5 },
  { id: "schedule", start: 0.5, end: 0.64 },
  { id: "budget", start: 0.64, end: 0.78 },
  { id: "page", start: 0.78, end: 0.92 },
  { id: "reveal", start: 0.92, end: 1 },
];

export const REWIND_TIMELINE_BEATS = REWIND_BEATS.filter(
  (beat) => beat.id !== "reveal",
);

/** Where a timeline jump lands inside each beat, once its content has settled. */
export const REWIND_BEAT_JUMP_POINT: Record<RewindBeatId, number> = {
  screen: 0,
  set: 0.2,
  breakdown: 0.42,
  schedule: 0.6,
  budget: 0.75,
  page: 0.88,
  reveal: 0.985,
};

/** Progress past which the hero counts as completed for analytics. */
export const REWIND_COMPLETED_AT = 0.97;

export const REWIND_SCENE_NUMBER = PIPELINE_DEMO_SCENE_NUMBER;
export const REWIND_TAKE = 3;
export const REWIND_SHOOT_DAY = 4;
export const REWIND_SHOOT_DAYS = 5;

/** Stripboard neighbours shown around scene 12, from the pipeline demo rows. */
export const REWIND_STRIP_SCENE_IDS = [
  "scene-11",
  "scene-12",
  "scene-13",
] as const;
export const REWIND_STRIP_DAY_ID = "day-04";
/** Scenes 11 to 13: ½ + 2⅛ + 1¾ pages. */
export const REWIND_DAY_PAGES = "4⅜";

/** Trust chips under the reveal CTAs (keys in home.hero.chips), plus rewind.reveal.chipCrew. */
export const REWIND_TRUST_CHIP_IDS = ["scriptYours", "noTraining"] as const;

/** Strip colors from the product app's scene color map (rivetai-frontend sceneColorFactory). */
export const REWIND_STRIP_COLORS: Record<
  (typeof REWIND_STRIP_SCENE_IDS)[number],
  string
> = {
  "scene-11": "#FF458B", // EXT. DAY
  "scene-12": "#668B8B", // INT. NIGHT
  "scene-13": "#FFD78B", // INT. CONTINUOUS
};

const demoTagColor = (id: string) =>
  DEMO_APPLIED_TAG_STRUCTURES.find((tag) => tag.id === id)?.color ?? "#94A3B8";

/** Category colors come from the existing demos. SFX is the platform default category color. */
export const REWIND_CATEGORY_COLORS: Record<RewindCategoryId, string> = {
  cast: demoTagColor("marcus"),
  props: demoTagColor("flashlight"),
  vfx: demoTagColor("rain"),
  set: demoTagColor("catwalk"),
  sound: demoTagColor("footsteps"),
  sfx: "#EFA919",
};

export type RewindTagConfig = {
  id: RewindTagId;
  category: RewindCategoryId;
  /** Category after the 1st AD's correction, when there is one. */
  correctedCategory?: RewindCategoryId;
  /** Heard, not seen: shown as a waveform chip at the frame edge. */
  offscreen?: boolean;
};

/** Order the tags lock on in the breakdown beat. */
export const REWIND_TAGS: RewindTagConfig[] = [
  { id: "marcus", category: "cast" },
  { id: "flashlight", category: "props" },
  { id: "rain", category: "vfx", correctedCategory: "sfx" },
  { id: "catwalk", category: "set" },
  { id: "elena", category: "cast" },
  { id: "handgun", category: "props" },
  { id: "laser", category: "props" },
  { id: "footsteps", category: "sound", offscreen: true },
];

export const REWIND_CORRECTED_TAG: RewindTagId = "rain";

/** Budget lines tied to the tags. Amounts reuse the pipeline demo where it has them. */
export const REWIND_BUDGET_LINES = [
  { id: "rain", category: "sfx", amount: 210_000 },
  { id: "catwalk", category: "set", amount: 85_000 },
  { id: "handgun", category: "props", amount: 18_500 },
  { id: "marcus", category: "cast", amount: 620_000 },
  { id: "elena", category: "cast", amount: 480_000 },
] as const satisfies ReadonlyArray<{
  id: RewindTagId;
  category: RewindCategoryId;
  amount: number;
}>;

/** Letterbox aspect on load. Scroll opens it to the full camera frame. */
export const REWIND_CINEMA_ASPECT = 2.39;

/** Share of the source frame width that must stay visible on narrow screens. */
export const REWIND_SAFE_WIDTH = 0.72;

/** Frames kept decoded around the playhead. */
export const REWIND_DECODE_CACHE = { desktop: 16, mobile: 24 } as const;

/** Frame loading passes: every 8th frame, then 4th, 2nd, then all. */
export const REWIND_LOAD_PASSES = [8, 4, 2, 1] as const;

export const REWIND_ANALYTICS_EVENTS = {
  beatViewed: "home_hero_beat_viewed",
  skipIntro: "home_hero_skip_intro",
  completed: "home_hero_completed",
  soundToggled: "home_hero_sound_toggled",
} as const;

export const REWIND_AFTER_HERO_ID = "home-after-hero";

/** Existing screenplay Courier stack used by every script demo. */
export const REWIND_SCREENPLAY_FONT_CLASS =
  "font-['Courier_New',Courier,monospace]";
